import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LayoutDashboard, ClipboardList, FolderOpen, FileText, BarChart3, ShieldCheck, LogOut, ChevronDown, ClipboardCheck, ListChecks } from "lucide-react";
import { roleLabel } from "@/lib/constants";

const tabs = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/audit", label: "Audit", icon: ClipboardCheck },
  { to: "/compliance", label: "Compliance", icon: ListChecks },
  { to: "/actions", label: "QIP Actions", icon: ClipboardList },
  { to: "/evidence", label: "Evidence", icon: FolderOpen },
  { to: "/sar", label: "SAR Builder", icon: FileText },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = user?.name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "U";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/85 backdrop-blur-md no-print">
        <div className="max-w-[1400px] mx-auto px-6 flex items-center h-14 gap-6">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-indigo-900 grid place-items-center text-white"><ShieldCheck className="h-4 w-4" /></div>
            <div className="font-bold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>QIP Studio</div>
            <Badge variant="outline" className="ml-2 text-[10px] font-mono border-slate-300 text-slate-600">v2.0 · SEP 2026</Badge>
          </div>
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {tabs.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}>
                <Icon className="h-4 w-4" />{label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 hover:bg-slate-50">
                  <div className="h-7 w-7 rounded-full bg-indigo-900 text-white grid place-items-center text-[11px] font-semibold">{initials}</div>
                  <div className="text-left leading-tight hidden sm:block">
                    <div className="text-xs font-semibold text-slate-900">{user?.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{roleLabel(user?.role)}</div>
                  </div>
                  <ChevronDown className="h-3 w-3 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="md:hidden border-t border-slate-200 px-4 py-2 flex gap-1 overflow-x-auto">
          {tabs.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium whitespace-nowrap ${isActive ? "bg-indigo-50 text-indigo-900" : "text-slate-600"}`}>
              <Icon className="h-3.5 w-3.5" />{label}
            </NavLink>
          ))}
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-8"><Outlet /></main>
    </div>
  );
}
